import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Loader2, ChevronRight, UserPlus, X, AlertCircle, Check, BookOpen } from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { SignedInAsBar } from '@/components/SignedInAsBar';

interface Candidate {
  personId: string;
  displayName: string;
}

interface AttachToRoundModalProps {
  roundId: string;
  groupId: string | null;
  isFinalized: boolean;
  onClose: () => void;
}

type Step = 'loading' | 'select' | 'confirm' | 'attaching' | 'error';

export function AttachToRoundModal({ roundId, groupId, isFinalized, onClose }: AttachToRoundModalProps) {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<Step>('loading');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/rounds/${roundId}/attach-candidates`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setCandidates(data.candidates ?? []);
        setStep('select');
      })
      .catch(() => {
        if (!cancelled) setStep('error');
      });
    return () => { cancelled = true; };
  }, [roundId]);

  const handleSelect = (c: Candidate) => {
    setSelected(c);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setStep('attaching');
    setError(null);
    try {
      const res = await apiRequest('POST', `/api/rounds/${roundId}/attach`, { personId: selected.personId });
      const data = await res.json();
      if (!data.success) {
        setError(data.error ?? 'Could not add round — please try again.');
        setStep('select');
        return;
      }
      if (data.personId) {
        queryClient.invalidateQueries({ queryKey: ['ledger-rounds', data.personId] });
        queryClient.invalidateQueries({ queryKey: ['ledger-stats', data.personId] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/user/groups'] });
      queryClient.invalidateQueries({ queryKey: ['user-active-rounds'] });

      if (isFinalized) {
        const dest = `/ledger?tab=history${data.groupId ? `&groupId=${data.groupId}` : ''}&claimedRoundId=${roundId}`;
        navigate(dest);
      } else {
        onClose();
      }
    } catch (err: any) {
      let msg = 'Could not add round — please try again.';
      try {
        const body = await err?.json?.();
        if (body?.error) msg = body.error;
      } catch {}
      if (msg.includes('already been claimed') || msg.includes('already have a linked')) {
        msg = 'This profile is already linked to another account. Contact your organizer.';
      }
      setError(msg);
      setStep('select');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="attach-modal-overlay"
    >
      <div
        className="bg-white rounded-t-2xl w-full max-w-md"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        onClick={e => e.stopPropagation()}
        data-testid="attach-modal"
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900 text-base">
              {step === 'confirm' ? 'Is this you?' : 'Who were you in this match?'}
            </h2>
            {step === 'select' && (
              <p className="text-xs text-gray-500 mt-0.5">Select your name to add this round to your history and stats.</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 p-1 -mr-1"
            data-testid="button-attach-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4">
          {step === 'loading' && (
            <div className="flex items-center justify-center py-8" data-testid="attach-loading">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-6" data-testid="attach-error">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-700">Could not load players</p>
              <p className="text-xs text-gray-400 mt-1">Check your connection and try again.</p>
              <button
                onClick={() => { setStep('loading'); }}
                className="mt-4 text-sm font-semibold text-emerald-700"
                data-testid="button-attach-retry"
              >
                Try again
              </button>
            </div>
          )}

          {(step === 'select' || step === 'attaching') && (
            <div className="space-y-2" data-testid="attach-candidate-list">
              <SignedInAsBar returnTo={`/rounds/${roundId}/live`} />
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 mb-3" data-testid="attach-error-msg">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {candidates.length === 0 ? (
                <div className="text-center py-4" data-testid="attach-no-candidates">
                  <p className="text-sm font-semibold text-gray-700 mb-1">All player slots are already linked.</p>
                  <p className="text-xs text-gray-400">Contact your organizer if this round should be in your Ledger.</p>
                </div>
              ) : (
                candidates.map(c => (
                  <button
                    key={c.personId}
                    onClick={() => handleSelect(c)}
                    disabled={step === 'attaching'}
                    className="w-full bg-gray-50 rounded-xl border border-gray-200 p-4 flex items-center justify-between active:bg-emerald-50 active:border-emerald-200 transition-colors disabled:opacity-50"
                    data-testid={`button-attach-candidate-${c.personId}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-emerald-700">{c.displayName.charAt(0).toUpperCase()}</span>
                      </div>
                      <span className="font-semibold text-gray-900 text-sm">{c.displayName}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </button>
                ))
              )}

              <div className="pt-2 border-t border-gray-100 mt-2">
                <p className="text-xs text-gray-400 text-center">
                  Not listed? Contact your organizer — they can add you before the next round.
                </p>
              </div>
            </div>
          )}

          {step === 'confirm' && selected && (
            <div className="space-y-4" data-testid="attach-confirm">
              <SignedInAsBar returnTo={`/rounds/${roundId}/live`} />
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                  <span className="text-lg font-black text-emerald-700">{selected.displayName.charAt(0).toUpperCase()}</span>
                </div>
                <p className="font-bold text-gray-900">{selected.displayName}</p>
                <p className="text-xs text-gray-500 mt-1">All their match history, Razzy stats, and rivalries will become yours.</p>
              </div>

              <button
                onClick={handleConfirm}
                className="w-full bg-[#064E3B] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 active:bg-emerald-800 transition-colors"
                data-testid="button-attach-confirm-yes"
              >
                <Check className="w-4 h-4" />
                Yes, that&apos;s me
              </button>
              <button
                onClick={() => { setStep('select'); setSelected(null); }}
                className="w-full text-gray-500 font-medium py-3 rounded-xl active:bg-gray-100 transition-colors text-sm"
                data-testid="button-attach-confirm-back"
              >
                Choose a different player
              </button>
            </div>
          )}

          {step === 'attaching' && (
            <div className="flex items-center justify-center py-6" data-testid="attach-attaching">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600 mr-2" />
              <p className="text-sm text-gray-600">Adding to your Ledger…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
